import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { getHelp } from "./helpRegistry";

export default function HelpButton() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const help = getHelp(loc.pathname);

  // ESC 关闭
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // 切换路由时关闭
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 w-12 h-12 rounded-full bg-blue-600 text-white text-xl shadow-lg hover:bg-blue-700 hover:shadow-xl transition z-40 flex items-center justify-center"
        title={help ? `这是什么页：${help.title}` : "帮助"}
        aria-label="帮助"
      >
        ❓
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={() => setOpen(false)}>
          <div
            role="dialog" aria-modal="true" aria-label="页面帮助"
            className="bg-white dark:bg-zinc-900 w-full sm:max-w-2xl sm:rounded-lg max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {help ? <HelpContent /> : <NoHelp path={loc.pathname} />}
          </div>
        </div>
      )}
    </>
  );

  function HelpContent() {
    if (!help) return null;
    return (
      <div>
        <header className="sticky top-0 bg-white dark:bg-zinc-900 border-b dark:border-zinc-800 px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{help.title}</h2>
          <button aria-label="关闭帮助" onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-600 text-2xl leading-none">×</button>
        </header>
        <div className="p-5 space-y-5 text-sm">
          <p className="text-base text-blue-700 dark:text-blue-300 font-medium">{help.oneLiner}</p>

          <Section title="🎯 这是什么">
            {help.whatItDoes.map((p, i) => (
              <p key={i} className="text-zinc-700 dark:text-zinc-300 leading-relaxed mb-2">{p}</p>
            ))}
          </Section>

          {help.howToUse && help.howToUse.length > 0 && (
            <Section title="📖 怎么用">
              <ol className="list-decimal pl-5 space-y-1.5 text-zinc-700 dark:text-zinc-300">
                {help.howToUse.map((step, i) => <li key={i}>{step}</li>)}
              </ol>
            </Section>
          )}

          {help.metrics && help.metrics.length > 0 && (
            <Section title="📊 关键指标">
              <ul className="space-y-1.5">
                {help.metrics.map((m, i) => (
                  <li key={i} className="text-zinc-700 dark:text-zinc-300">
                    <b className="text-zinc-900 dark:text-zinc-100">{m.name}</b>
                    <span className="text-zinc-500 mx-1">—</span>
                    {m.meaning}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {help.caveats && help.caveats.length > 0 && (
            <Section title="⚠️ 注意事项">
              <ul className="list-disc pl-5 space-y-1.5 text-amber-700 dark:text-amber-300">
                {help.caveats.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </Section>
          )}

          {help.faq && help.faq.length > 0 && (
            <Section title="❓ 常见问题">
              <div className="space-y-3">
                {help.faq.map((f, i) => (
                  <div key={i}>
                    <div className="font-medium text-zinc-900 dark:text-zinc-100">Q：{f.q}</div>
                    <div className="text-zinc-700 dark:text-zinc-300 mt-1">A：{f.a}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {help.related && help.related.length > 0 && (
            <Section title="🔗 相关页面">
              <div className="flex flex-wrap gap-2">
                {help.related.map((r, i) => (
                  <Link
                    key={i}
                    to={r.path}
                    className="px-3 py-1 text-xs rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-950/40 dark:text-blue-300"
                  >
                    {r.label} →
                  </Link>
                ))}
              </div>
            </Section>
          )}

          <div className="text-xs text-zinc-400 pt-3 border-t dark:border-zinc-800">
            按 ESC 关闭 · 请结合本次配置、数据日期与执行记录理解结果。
          </div>
        </div>
      </div>
    );
  }
}

function NoHelp({ path }: { path: string }) {
  return (
    <div className="p-6 text-center">
      <h3 className="text-lg font-semibold mb-2">暂无帮助</h3>
      <p className="text-sm text-zinc-500">
        当前页面暂未提供专用说明。可以通过“更多工具”查找对应功能，或记录页面路径 {path} 反馈问题。
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="font-medium text-zinc-900 dark:text-zinc-100 mb-2">{title}</h3>
      {children}
    </section>
  );
}
